import { Activity, QuizFormData } from '../types';

export type QuizV2CreateQuizPayload = {
    title: string;
    type: 'ASSESSMENT';
    startTime: string | null;
    endTime: string | null;
    durationInMinutes: number | null;
    maxAttemptCount: number;
    evaluationType: 'GRADED' | 'UNGRADED';
    accessType: 'OPEN' | 'INSTITUTION';
    /** Library quiz V2 (video/chapter/textbook) Draft/Active status. Set by library payload builders only. */
    status?: 'ACTIVE' | 'DRAFT';
};

export type QuizV2CreateQuestionOption = {
    text: string;
    order: number;
    isCorrect: boolean;
    /** DB option id for existing options (used to attach `id` in update payloads); omitted for new options. */
    sourceOptionId?: number;
};

export type QuizV2CreateQuestion = {
    type: 'MULTIPLE_CHOICE_QUESTION';
    content: string;
    subType: 'SINGLE_CORRECT' | 'MULTIPLE_CORRECT';
    imageUrl?: string;
    options: QuizV2CreateQuestionOption[];
};

export type QuizV2CreateSectionItem = {
    type: 'QUESTION';
    order: number;
    question: QuizV2CreateQuestion;
    /**
     * DB section item id for questions that already exist, like `sourceOptionId`. Absent
     * for questions added during this edit. Update payloads match on this rather than on
     * position, so reordering or removing a question cannot attach one question's id to
     * another's text.
     */
    sourceItemId?: number;
};

export type QuizV2CreateSection = {
    title: string;
    sectionType: 'DEFAULT' | 'ARTICLE_LINKED_SECTION';
    order: number;
    items: QuizV2CreateSectionItem[];
    /** DB section id for sections that already exist; absent for ones added during this edit. */
    sourceSectionId?: number;
    /**
     * Top-level article id for quiz-svc create (ArticeRefRequestDto).
     * Prefer this over nested `article` for POST create payloads.
     */
    articleId?: number;
    /** Nested article ref used by some update/detail shapes (SectionDto). */
    article?: { articleId: number };
};

export type QuizV2CreateRequestBody = {
    quiz: QuizV2CreateQuizPayload;
    sections: QuizV2CreateSection[];
};

function mapActivityItemToSectionItems(activity: Activity): QuizV2CreateSectionItem[] {
    const questions = activity.items
        .filter((item) => item.type === 2)
        .sort((a, b) => a.sortIndex - b.sortIndex);

    return questions.map((item, qIndex) => {
        const subType: 'SINGLE_CORRECT' | 'MULTIPLE_CORRECT' = item.isMultiple ? 'MULTIPLE_CORRECT' : 'SINGLE_CORRECT';
        const options: QuizV2CreateQuestionOption[] = item.textOptions.map((opt, i) => ({
            text: opt.text,
            order: i + 1,
            isCorrect: item.rightAnswer.includes(opt.originId),
            ...(typeof opt.sourceOptionId === 'number' ? { sourceOptionId: opt.sourceOptionId } : {}),
        }));

        const question: QuizV2CreateQuestion = {
            type: 'MULTIPLE_CHOICE_QUESTION',
            content: item.title,
            subType,
            options,
        };

        if (item.image) {
            question.imageUrl = item.image;
        }

        return {
            type: 'QUESTION',
            order: qIndex + 1,
            question,
            ...(typeof item.originId === 'number' ? { sourceItemId: item.originId } : {}),
        };
    });
}

function parseDurationMinutes(duration: QuizFormData['duration']): number | null {
    const n = typeof duration === 'string' ? parseInt(duration, 10) : Number(duration);
    if (!Number.isFinite(n) || n < 0) {
        return null;
    }
    return n === 0 ? null : n;
}

export type BuildQuizV2CreatePayloadOptions = {
    /** Map `restrictNonInstitutional` to quiz-svc `accessType` (OPEN vs INSTITUTION). */
    applyUserQuizAccessType?: boolean;
};

/** Maps admin quiz form state to POST /api/v2/admin/quiz/create body. */
export function buildQuizV2CreatePayload(
    values: QuizFormData,
    options?: BuildQuizV2CreatePayloadOptions
): QuizV2CreateRequestBody {
    const { title, isGraded, startDate, endDate, duration, activities } = values;

    const evaluationType: 'GRADED' | 'UNGRADED' = isGraded === 'graded' ? 'GRADED' : 'UNGRADED';

    const accessType: 'OPEN' | 'INSTITUTION' = options?.applyUserQuizAccessType
        ? (values.restrictNonInstitutional ? 'INSTITUTION' : 'OPEN')
        : 'OPEN';

    const quiz: QuizV2CreateQuizPayload = {
        title: title.trim(),
        type: 'ASSESSMENT',
        startTime: startDate ? startDate.toISOString() : null,
        endTime: endDate ? endDate.toISOString() : null,
        durationInMinutes: parseDurationMinutes(duration),
        maxAttemptCount: 1,
        evaluationType,
        accessType,
    };

    const sections: QuizV2CreateSection[] = [...activities]
        .sort((a, b) => a.sortIndex - b.sortIndex)
        .map((activity, i) => ({
            title: activity.title,
            sectionType: 'DEFAULT',
            order: i + 1,
            items: mapActivityItemToSectionItems(activity),
            ...(typeof activity.originId === 'number' ? { sourceSectionId: activity.originId } : {}),
        }))
        .filter((s) => s.items.length > 0);

    return { quiz, sections };
}
